import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2, ArrowRight, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
    { key: "submitted", label: "Submitted", desc: "Your claim is logged and has a case owner." },
    { key: "under_review", label: "Under review", desc: "Our clinical team validates the claim against the original design file." },
    { key: "approved", label: "Approved — in production", desc: "Your replacement is being manufactured at our nearest laboratory." },
    { key: "replacement_shipped", label: "Replacement shipped", desc: "On its way — delivered free of charge, anywhere in the world." },
    { key: "resolved", label: "Resolved", desc: "Replacement delivered. Your warranty continues on the new restoration." },
];

const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
};

/** ToothPassport claim tracker — public live status page: /claim/{claimNumber}. */
export default function ClaimTracker() {
    const { claimNumber } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(() => {
        setRefreshing(true);
        axios.get(`${API}/warranty/claims/${encodeURIComponent(claimNumber)}`)
            .then(({ data }) => { setData(data); setError(null); })
            .catch((e) => setError(e?.response?.data?.detail || "Claim lookup failed"))
            .finally(() => setRefreshing(false));
    }, [claimNumber]);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const rejected = data?.status === "rejected";
    const currentIdx = data ? STEPS.findIndex((s) => s.key === data.status) : -1;
    const historyByStatus = Object.fromEntries((data?.status_history || []).map((h) => [h.status, h]));

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-[#0e2436] to-[#0B1120] text-white flex items-center justify-center px-4 py-16" data-testid="claim-tracker-page">
            <div className="w-full max-w-xl">
                <div className="text-center mb-6">
                    <div className="font-mono-tr text-[11px] tracking-[0.32em] uppercase text-cyan-300">ToothPassport™ · Warranty claim</div>
                    <div className="text-[12px] text-slate-400 mt-1">Live claim tracking · Toothrocket Dental Labs</div>
                </div>

                {!data && !error && (
                    <div className="rounded-3xl bg-white/[0.05] border border-white/10 p-14 text-center">
                        <Loader2 className="animate-spin mx-auto text-cyan-300" />
                        <div className="mt-3 text-[13px] text-slate-300">Loading your claim…</div>
                    </div>
                )}

                {error && !data && (
                    <div className="rounded-3xl bg-white/[0.05] border border-rose-400/30 p-10 text-center" data-testid="ct-error">
                        <ShieldAlert size={40} className="mx-auto text-rose-400 mb-3" />
                        <div className="text-[17px] font-semibold">No claim found</div>
                        <p className="mt-2 text-[13px] text-slate-400">We couldn&rsquo;t find claim <span className="font-mono-tr text-slate-200">{claimNumber}</span>. Check the number in your confirmation email.</p>
                    </div>
                )}

                {data && (
                    <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-cyan-500/10" data-testid="ct-card">
                        <div className={`px-8 py-6 flex items-center gap-4 ${rejected ? "bg-rose-500/15 border-b border-rose-400/25" : "bg-cyan-500/15 border-b border-cyan-400/25"}`}>
                            {rejected ? <XCircle size={26} className="text-rose-300" /> : <ShieldCheck size={26} className="text-cyan-300" />}
                            <div className="flex-1">
                                <div className="text-[18px] font-semibold tracking-tight" data-testid="ct-status">{data.status_label}</div>
                                <div className="text-[12.5px] text-slate-300/90 font-mono-tr" data-testid="ct-claim-number">{data.claim_number}</div>
                            </div>
                            <button onClick={load} data-testid="ct-refresh" className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition" title="Refresh">
                                <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
                            </button>
                        </div>

                        <div className="bg-white/[0.04] backdrop-blur px-8 py-7">
                            {data.demo && (
                                <div className="mb-5 rounded-xl bg-cyan-400/10 border border-cyan-400/25 px-4 py-3 text-[12.5px] text-cyan-100" data-testid="ct-demo-note">
                                    <strong>This is a demo claim</strong> on the live demo passport — feel free to explore.
                                </div>
                            )}
                            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-[13.5px] mb-7">
                                <div><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Case number</dt><dd className="font-mono-tr text-cyan-200">{data.case_number}</dd></div>
                                <div><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Submitted</dt><dd>{fmtDate(data.created_at)}</dd></div>
                                <div><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Restoration</dt><dd>{data.restoration}</dd></div>
                                <div><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Country</dt><dd>{data.country}</dd></div>
                            </dl>

                            {(data.photos || []).length > 0 && (
                                <div className="mb-7" data-testid="ct-photos">
                                    <div className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-2">Attached photos</div>
                                    <div className="flex gap-2 flex-wrap">
                                        {data.photos.map((pid) => (
                                            <a key={pid} href={`${API}/warranty/claims/${encodeURIComponent(data.claim_number)}/photos/${pid}`} target="_blank" rel="noreferrer">
                                                <img src={`${API}/warranty/claims/${encodeURIComponent(data.claim_number)}/photos/${pid}`} alt="Damaged restoration"
                                                    className="w-20 h-20 rounded-xl object-cover border border-white/15 hover:border-cyan-300 transition" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {rejected ? (
                                <div className="rounded-xl bg-rose-500/10 border border-rose-400/25 px-5 py-4" data-testid="ct-rejected">
                                    <div className="text-[14px] font-semibold text-rose-200">Claim rejected</div>
                                    <p className="mt-1.5 text-[12.5px] text-slate-300 leading-relaxed">
                                        {historyByStatus.rejected?.note || "This claim falls outside warranty coverage. Contact us if you believe this is a mistake."}
                                    </p>
                                </div>
                            ) : (
                                <ol className="relative" data-testid="ct-timeline">
                                    {STEPS.map((s, i) => {
                                        const done = i < currentIdx;
                                        const current = i === currentIdx;
                                        const h = historyByStatus[s.key];
                                        return (
                                            <li key={s.key} className="relative pl-10 pb-7 last:pb-0" data-testid={`ct-step-${s.key}`}>
                                                {i < STEPS.length - 1 && (
                                                    <span className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${done ? "bg-emerald-400/70" : "bg-white/10"}`} />
                                                )}
                                                <span className={`absolute left-0 top-0.5 w-6 h-6 rounded-full grid place-items-center border-2 ${
                                                    done ? "bg-emerald-400/20 border-emerald-400" : current ? "bg-cyan-400/20 border-cyan-300" : "bg-white/5 border-white/15"}`}>
                                                    {done && <CheckCircle2 size={13} className="text-emerald-300" />}
                                                    {current && (
                                                        <span className="relative flex h-2 w-2">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-60" />
                                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-300" />
                                                        </span>
                                                    )}
                                                </span>
                                                <div className={`text-[14px] font-semibold ${done || current ? "text-white" : "text-slate-500"}`}>{s.label}</div>
                                                <div className={`text-[12px] leading-relaxed ${done || current ? "text-slate-400" : "text-slate-600"}`}>{s.desc}</div>
                                                {h && <div className="mt-1 text-[11px] font-mono-tr text-slate-500">{fmtDate(h.at)}{h.note ? ` · ${h.note}` : ""}</div>}
                                            </li>
                                        );
                                    })}
                                </ol>
                            )}

                            <div className="mt-7 pt-5 border-t border-white/10 flex flex-wrap gap-3">
                                <Link to={`/warranty/${encodeURIComponent(data.case_number)}`} data-testid="ct-passport-link"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/15 hover:border-cyan-300 text-[12.5px] font-medium">
                                    <ShieldCheck size={13} /> Open ToothPassport
                                </Link>
                                <Link to={`/warranty/${encodeURIComponent(data.case_number)}#terms`} data-testid="ct-terms-link"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/15 hover:border-cyan-300 text-[12.5px] font-medium">
                                    Warranty terms
                                </Link>
                                <Link to="/" data-testid="ct-home-link"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-400 text-[#0B1120] hover:bg-cyan-300 text-[12.5px] font-semibold">
                                    toothrocket.com <ArrowRight size={13} />
                                </Link>
                            </div>
                        </div>

                        <div className="bg-[#0B1120] px-8 py-3.5 flex items-center justify-between text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">
                            <span>Toothrocket Dental Labs</span>
                            <span>Updates every 30s · live</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
